import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Upload, Mail, CheckCircle2, Shield, Clock, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import ConfigureDocument from '../components/ConfigureDocument';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentData, setDocumentData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes

  useEffect(() => {
    fetchCompletedCount();
  }, []);

  useEffect(() => {
    if (completedCount > 0) {
      let start = 0;
      const duration = 2000; // 2 seconds
      const increment = completedCount / (duration / 16); // 60fps
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= completedCount) {
          setDisplayCount(completedCount);
          clearInterval(timer);
        } else {
          setDisplayCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }
  }, [completedCount]);

  const fetchCompletedCount = async () => {
    try {
      const response = await axios.get(`${API}/stats/completed`);
      setCompletedCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch completed count:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error(`Datei ist zu groß! Maximale Größe: 20 MB`);
        return;
      }
      setFile(selectedFile);
    } else {
      toast.error('Bitte wählen Sie eine PDF-Datei aus');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      if (droppedFile.size > MAX_FILE_SIZE) {
        toast.error(`Datei ist zu groß! Maximale Größe: 20 MB`);
        return;
      }
      setFile(droppedFile);
    } else {
      toast.error('Bitte laden Sie eine PDF-Datei hoch');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Show success state for 4-5 seconds before transitioning
      toast.success('PDF erfolgreich hochgeladen!');
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      setDocumentData(response.data);
    } catch (error) {
      toast.error('Fehler beim Hochladen der Datei');
      console.error(error);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  if (documentData) {
    return <ConfigureDocument documentData={documentData} />;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="h-9 w-9 sm:h-10 sm:w-10 bg-zinc-950 rounded-lg flex items-center justify-center shadow-sm">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                SignFlow
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                data-testid="new-document-button"
                onClick={() => window.location.reload()}
                className="bg-zinc-950 text-white hover:bg-zinc-800 gap-1.5 sm:gap-2 text-xs sm:text-sm h-8 sm:h-9 px-2.5 sm:px-4 rounded-lg shadow-sm"
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Neu
              </Button>
              <Button
                data-testid="back-to-hvkurz-button"
                variant="outline"
                onClick={() => window.open('https://www.hvkurz.de', '_blank')}
                className="text-xs sm:text-sm h-8 sm:h-9 px-2.5 sm:px-4 rounded-lg border-zinc-300"
              >
                <span className="hidden sm:inline">zur Verwaltung</span>
                <span className="sm:hidden">HV</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Dokumente digital unterschreiben
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-zinc-600 max-w-2xl">
              Laden Sie Ihr PDF hoch, senden Sie es per E-Mail und erhalten Sie das unterschriebene Dokument zurück. 
              Einfach, sicher und professionell.
            </p>

            {/* Upload Area */}
            <Card className="p-8 border-zinc-200 shadow-sm">
              <div
                data-testid="upload-dropzone"
                className={`upload-dropzone border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                  isDragging ? 'dragging border-orange-600 bg-orange-50' : 'border-zinc-300'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-zinc-400" />
                <h3 className="text-xl font-semibold mb-2">PDF hier ablegen</h3>
                <p className="text-zinc-600 mb-2">oder klicken Sie, um eine Datei auszuwählen</p>
                <p className="text-xs text-zinc-500 mb-6">Maximale Dateigröße: 20 MB</p>
                <input
                  data-testid="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    data-testid="select-file-button"
                    variant="secondary"
                    className="cursor-pointer"
                    type="button"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    Datei auswählen
                  </Button>
                </label>
                {file && (
                  <p className="mt-4 text-sm text-zinc-700 font-medium" data-testid="selected-file-name">
                    Ausgewählt: {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                )}
              </div>
              {file && (
                <>
                  {uploading && uploadProgress > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm text-zinc-600 mb-2">
                        <span>Wird hochgeladen...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full h-2 bg-zinc-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-600 transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <Button
                    data-testid="upload-button"
                    onClick={handleUpload}
                    disabled={uploading}
                    className="w-full mt-4 bg-zinc-950 text-white hover:bg-zinc-800 h-12 text-base font-medium"
                  >
                    {uploading ? 'Wird hochgeladen...' : 'PDF hochladen'}
                  </Button>
                </>
              )}
            </Card>
          </div>

          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1666018215790-867b14fe4822?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Professionelles Unterschreiben"
              className="rounded-xl shadow-2xl shadow-zinc-900/10"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-6 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-8 border-zinc-200 hover:border-zinc-400 transition-colors duration-300">
            <Shield className="h-10 w-10 text-orange-600 mb-4" />
            <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Automatische Erkennung
            </h3>
            <p className="text-zinc-600 leading-relaxed">
              Unterschriftsfelder werden automatisch erkannt und können beliebig angepasst werden.
            </p>
          </Card>

          <Card className="p-8 border-zinc-200 hover:border-zinc-400 transition-colors duration-300">
            <Mail className="h-10 w-10 text-orange-600 mb-4" />
            <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              E-Mail Versand
            </h3>
            <p className="text-zinc-600 leading-relaxed">
              Senden Sie Dokumente direkt per E-Mail an Empfänger. Nach der Unterschrift erhalten Sie das fertige Dokument.
            </p>
          </Card>

          <Card className="p-8 border-zinc-200 hover:border-zinc-400 transition-colors duration-300">
            <Clock className="h-10 w-10 text-orange-600 mb-4" />
            <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              30 Tage Gültigkeit
            </h3>
            <p className="text-zinc-600 leading-relaxed">
              Dokumente bleiben 30 Tage lang gültig. Mehrere Personen können nacheinander unterschreiben.
            </p>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white mt-24">
        <div className="container mx-auto px-6 py-12">
          <div className="text-center text-sm text-zinc-500 space-y-4">
            <div className="text-zinc-400">
              <span className="font-semibold text-zinc-600">{displayCount.toLocaleString('de-DE')}</span> Dokumente unterschrieben
            </div>
            <p>Entwickelt von Scholze-Kurz & Kurz Immobilien GmbH</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://www.hvkurz.de/impressum"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Impressum
              </a>
              <span>|</span>
              <a
                href="https://www.hvkurz.de/datenschutzerklaerung/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Datenschutz
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}